import Icon from 'common/Icon'
import React, { Children, createContext, useContext } from 'react'
import './Card.scss'
import colors from 'component_library/theme/_colorExports.module.scss'
import { reactNodeIsOfType } from 'component_library/utils'

export type BorderTypes = 'dashed' | 'solid'
export type CardVariants = 'default' | 'info' | 'warning' | 'error'
type AlertVariants = 'info' | 'warning'

export interface CardContextInterface {
  borderStyle: BorderTypes
  cardVariant: CardVariants
}

export const CardContext = createContext<CardContextInterface | null>(null)

const useCardContext = () => {
  const context = useContext(CardContext)

  if (!context) {
    throw new Error(
      'Card Alert cannot be rendered outside of the Card Component'
    )
  }

  return context
}

type CardProps = {
  borderStyle?: BorderTypes
  cardVariant?: CardVariants
  loading?: boolean
  className?: string
  onClick?: () => void
  disabled?: boolean
  children: React.ReactNode
}

/**
 * A commonly used Thriveworks container component. Takes children and renders them with the proper spacing.
 ** Completely unopinionated about what you put inside of it, or how you style it -- except for CardAlerts as children.
 ** When a `<CardAlert>` component is used inside of this, it will listen for the borderStyle & cardVariant prop from this component and render itself accordingly.
 * @param borderStyle 'solid' or 'dashed'. Defines the border-style property.
 * @param cardVariant 'default', 'info', warning', or 'error'. Determines border and background color of the card.
 * @param loading The boolean condition that will display the card's loading state.
 * @param onClick An optional click event to the card
 * @author Brendan Roman
 */
export const Card: React.FC<CardProps> = ({
  borderStyle = 'solid',
  cardVariant = 'default',
  loading = false,
  onClick,
  disabled = false,
  children,
  className = '',
}) => {
  let containsAlert = false

  const nonAlertChildren: React.ReactNode[] = []
  // this is an array instead of a singular node, beacuse the end user may want
  // to conditionally render alerts rather than use ternaries in CardAlert
  // Props. This allows that freedom.
  const alertChildren: React.ReactNode[] = []

  Children.forEach(children, (child) => {
    if (!reactNodeIsOfType(child, CardAlert)) {
      nonAlertChildren.push(child)
      return
    }

    containsAlert = true
    alertChildren.push(child)
  })

  if (loading) {
    return <div className="ClCard--loading"></div>
  }

  if (containsAlert && alertChildren.length) {
    return (
      <CardContext.Provider value={{ borderStyle, cardVariant }}>
        <div
          className={`ClCard ClCard--hasAlert ClCard--${borderStyle} ClCard--${cardVariant} ${className}`}
        >
          {nonAlertChildren}
        </div>
        {alertChildren}
      </CardContext.Provider>
    )
  }

  return (
    <div
      className={`ClCard ClCard--noAlert ClCard--${borderStyle} ClCard--${cardVariant} ${className}`}
      onClick={() => {
        onClick && !disabled && onClick()
      }}
    >
      {children}
    </div>
  )
}

type CardAlertProps = {
  alertVariant?: AlertVariants
  showIcon?: boolean
  className?: string
  children: React.ReactNode
}

/**
 * An optional child of the Card Component. Notifies users of important alert messages pertaining to the information on the Card.
 ** This component listens for the props of its parent, and renders its border color and style accordingly.
 ** This component will throw an `Error` if not used as a child of `<Card />`.
 ** This  affixes itself to the bottom of the Card Component, even if you place it before the main content.
 * @param alertVariant The type of alert you wish to render. "info" or "warning".
 * @param showIcon true or false, whether you want the icon for the variant to show.
 * @param className Any additional classnames you want to add to the outer-most div of this component.
 * @author Brendan Roman
 */
export const CardAlert: React.FC<CardAlertProps> = ({
  alertVariant = 'info',
  showIcon = true,
  className = '',
  children,
}) => {
  const { borderStyle, cardVariant } = useCardContext()

  return (
    <div
      className={`CardAlert CardAlert--${alertVariant} CardAlert--${borderStyle} CardAlert--cardVariant--${cardVariant} ${className}`}
    >
      {showIcon ? (
        <div className={`CardAlert__icon CardAlert__icon--${alertVariant}`}>
          <Icon
            icon={alertVariant}
            fill={alertVariant === 'warning' ? colors.orange : colors.blue}
            className={`CardAlert__icon CardAlert__icon--${alertVariant}`}
          />
        </div>
      ) : null}
      {children}
    </div>
  )
}
